// Growem - per-exercise target resolver for the Sesion "hoy:" chip.
//
// Precedence (highest first). Each tier only fires when the one above it has no answer:
//   0. OVERRIDE      - a manual per-day weight the user typed (d.weights). Always wins.
//   -  REPS/UNMAPPED - reps/time cue lifts (Pallof, dead bug, plank, carry) keep their plan cue (SEED).
//   1. HISTORY       - a real logged top set for THIS exercise before the viewed date. Shows the last
//                      lift ("ult:") and a double-progression target that is ALWAYS greater.
//   2a. SUGGEST      - no own history, but a RELATED lift has LOGGED history: a conservative estimate
//                      from that lift's real e1RM ("basado en tu <lift> de <w> kg").
//   -  OWN-SEED      - THIS lift has its own concrete plan weight ("41 kg x 8-10"): show it (SEED).
//                      Ranked ABOVE the estimate tiers so a coach-authored number is never downgraded
//                      to a lower guess (e.g. goblet 20 kg, DB curl 12.5 kg must win over Tier 2c).
//   2b. SUGGEST-SEED - a RELATED lift has only a PLAN-SEED weight (never logged): estimate from that
//                      plan number, framed honestly as a plan value, NOT an achieved "marca".
//   2c. SUGGEST-PROFILE - no lift proxy at all, but the lift is free-weight and we know the user's
//                      bodyweight/sex/level: a conservative starting weight from published strength
//                      standards ("sugerido inicial ... segun tu peso"). See strengthStandards.ts.
//   3. FIRST         - nothing to go on: an honest cue ("elige un peso dejando 2 reps en reserva").
//
// The estimates are deliberately conservative (new movement = technique-limited): low ratio, sized to
// the TOP of the rep range at 2-3 RIR, rounded DOWN. All estimate tiers render dashed + editable.

import type { Exercise } from '../types'
import { groupOf, isRepsOnly, userPlanBundle, PLAN_V1, PLAN_V2 } from '../data/plan'
import { RELATED_ESTIMATE, type RelatedSource } from './relatedness'
import { repRange, stepFor, nextTargetFromHistory } from './rules'
import { lastLoggedSet, e1rm, type HistoryIndex, type TopSet } from '../lib/strength'
import { profileEstimate, machineReference, ASSISTANCE_EXERCISES, STARTING_STANDARDS, type EstimateProfile } from './strengthStandards'

/** Which visual state the Sesion target row should render, plus the data each state needs. */
export interface TargetResolution {
  kind: 'override' | 'history' | 'suggest' | 'suggest-seed' | 'suggest-profile' | 'suggest-reference' | 'seed' | 'first'
  target: string                 // the "hoy:" / suggestion string ('' for first-time)
  ph: { w: string; r: string }   // S1/S2/S3 input placeholders
  lastStr?: string               // history: real last-set text for the "ult:" badge (value only)
  cue?: string                   // first: the first-time cue line
  basisLift?: string             // suggest / suggest-seed: related lift the estimate is based on
  basisWeight?: number           // suggest: that lift's LOGGED weight; suggest-seed: its PLAN-seed weight
  bodyWeight?: number            // suggest-profile: the bodyweight the estimate is based on
  perHand?: boolean              // any suggest kind: estimate is per dumbbell/hand
}

/** Integer, or one-decimal (e.g. 31.5) - never a long float from the e1RM math. */
export function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10)
}

// Short numeric hints for the set inputs, from a target string.
// "127-130 kg x 8-10" -> { w: "127", r: "8-10" };  "15 reps controladas" -> { w: "", r: "15" }
export function targetPlaceholders(t: string): { w: string; r: string } {
  const norm = (t || '').replace(',', '.')
  const w = /kg/i.test(norm) ? (norm.match(/(\d+(?:\.\d+)?)/)?.[1] ?? '') : ''
  const r = norm.match(/[×x]\s*(\d+(?:[–-]\d+)?)/)?.[1] ?? norm.match(/(\d+)\s*reps/i)?.[1] ?? ''
  return { w, r }
}

function repStrOf(ex: Exercise): string {
  const [lo, hi] = repRange(ex)
  return lo === hi ? String(hi) : `${lo}-${hi}`
}

/** "120 kg × 8 · RIR 1" or "12 reps" - the value part of the "últ:" badge (no prefix). */
function fmtLast(ts: TopSet): string {
  if (ts.repsOnly || ts.w == null) return `${ts.r} reps`
  return `${fmtNum(ts.w)} kg × ${ts.r}${ts.rir != null ? ` · RIR ${ts.rir}` : ''}`
}

/**
 * Parse a KG-QUALIFIED plan-seed string into {weight, reps}. Only a number immediately followed by
 * "kg" counts as a weight, so a bare rep range like "carga solida x 6-8" yields NO seed (guards
 * against grabbing a rep count as a fake load). Reps are read from the "x N" that follows the weight;
 * absent, default to 10. Examples: "50 kg × 8" -> {50, 8}; "20 kg/lado × 12-15" -> {20, 12};
 * "127 kg" -> {127, 10}; "carga solida x 6-8" -> null; "progresa cada semana" -> null.
 */
function parseSeed(str: string | undefined): { w: number; r: number } | null {
  if (!str) return null
  const norm = str.replace(',', '.')
  const m = norm.match(/(\d+(?:\.\d+)?)\s*kg/i)
  if (!m || m.index == null) return null
  const w = Number(m[1])
  if (!(w > 0)) return null
  const after = norm.slice(m.index + m[0].length)
  const rm = after.match(/[×x]\s*(\d+)/i)
  const r = rm ? Number(rm[1]) : 10
  return { w, r }
}

/**
 * Find a lift's plan-seed weight by NAME. A logged-in user with a generated PlanBundle is scanned
 * against THEIR plan only (their seeds are methodology-sanctioned declared marks; V1/V2 are
 * Sebastian's data and must never leak into another user's estimates). On the static path the scan is
 * version-agnostic across BOTH built-in plans - a plan seed is the user's own last mark regardless of
 * plan era, and many RELATED source lifts (multipress, Press militar sentado, Curl femoral, ...)
 * exist ONLY in PLAN_V1. Prefers V2 then V1; reads `last:` before the prescribed `t:`. Superset
 * children (pair) are scanned via their `t:`.
 */
export function findPlanSeed(name: string): { w: number; r: number } | null {
  const bundle = userPlanBundle()
  const scans = bundle ? [bundle.days] : [PLAN_V2, PLAN_V1]
  for (const plan of scans) {
    for (const day of plan) {
      for (const ex of day.ex) {
        if (ex.n === name) {
          const s = parseSeed(ex.last) ?? parseSeed(ex.t)
          if (s) return s
        }
        if (ex.pair) {
          for (const child of ex.pair) {
            if (child.n === name) { const s = parseSeed(child.t); if (s) return s }
          }
        }
      }
    }
  }
  return null
}

/** Shared e1RM inversion for Tier 2a (logged basis) and Tier 2b (plan-seed basis): scale the basis
 *  e1RM by the relatedness ratio, invert Epley at the TOP of the rep range with a conservative RIR 2,
 *  round DOWN to the machine step. Returns null if the result is non-positive. */
function buildEstimate(ex: Exercise, src: RelatedSource, basisE1rm: number, basisW: number, kind: 'suggest' | 'suggest-seed'): TargetResolution | null {
  const [lo, hi] = repRange(ex)
  const step = stepFor(ex.n)
  const targetE1rm = basisE1rm * src.ratio
  const rEff = hi + 2
  let w = targetE1rm / (1 + rEff / 30)
  w = Math.floor(w / step) * step
  if (w <= 0) return null
  const repStr = lo === hi ? String(hi) : `${lo}-${hi}`
  return {
    kind,
    target: `~${fmtNum(w)} kg${src.perHand ? ' por mano' : ''} × ${repStr}`,
    ph: { w: fmtNum(w), r: repStr },
    basisLift: src.name,
    basisWeight: basisW,
    perHand: !!src.perHand,
  }
}

/**
 * Tier 2a: estimate from a RELATED lift's LOGGED e1RM (first listed source with real history wins).
 * Returns null when no rule exists or no source has been logged - the caller then tries Tier 2b/2c.
 */
export function relatedEstimate(name: string, history: HistoryIndex, beforeDate: string, ex: Exercise): TargetResolution | null {
  const rule = RELATED_ESTIMATE[name]
  if (!rule) return null
  for (const src of rule.sources) {
    const s = lastLoggedSet(history, src.name, beforeDate)
    if (!s || s.e1rm == null || s.w == null) continue
    const est = buildEstimate(ex, src, s.e1rm, s.w, 'suggest')
    if (est) return est
  }
  return null
}

/**
 * Tier 2b: estimate from a RELATED lift's PLAN-SEED weight when it has NO logged history (first source
 * with a kg-qualified seed wins). The seed is a coach-prescribed number the user may never have
 * actually lifted, so the caller frames it as a plan value, NOT an achieved mark.
 */
export function relatedSeedEstimate(name: string, ex: Exercise): TargetResolution | null {
  const rule = RELATED_ESTIMATE[name]
  if (!rule) return null
  for (const src of rule.sources) {
    const seed = findPlanSeed(src.name)
    if (!seed) continue
    // D4: el seed de plan es una PRESCRIPCION del coach, no una ejecucion del usuario. Por eso el
    // rir queda en su default 0 A PROPOSITO: la imputacion de esfuerzo tipico (RIR mediano) aplica
    // solo a ejecuciones reales logueadas, jamas a un numero que el usuario quiza nunca levanto
    // (el test Tier 2b con ph.w = 35 clava este comportamiento).
    const est = buildEstimate(ex, src, e1rm(seed.w, seed.r), seed.w, 'suggest-seed')
    if (est) return est
  }
  return null
}

/**
 * Resolve the per-exercise target for the viewed day. `beforeDate` is the VIEWED date (strict "<"
 * history boundary), `override` is d.weights[weightKey(...)] (Tier 0). `profile` + `currentBw` +
 * `todayIso` power the Tier-2c profile estimate; omit them (as unit tests may) to disable Tier 2c.
 * Pure and cheap: reads a pre-built, memoized HistoryIndex, so it can run per render.
 */
export function resolveTarget(args: {
  history: HistoryIndex
  ex: Exercise
  beforeDate: string
  override?: string
  profile?: EstimateProfile | null
  currentBw?: number | null
  todayIso?: string
}): TargetResolution {
  const { history, ex, beforeDate, override, profile, currentBw, todayIso } = args
  const name = ex.n

  // Tier 0 - manual per-day override always wins.
  if (override && override.trim()) {
    return { kind: 'override', target: override, ph: targetPlaceholders(override) }
  }

  // Reps-only / unmapped lifts (Pallof, dead bug, plank, carry) carry no load: keep the plan cue and
  // never synthesize a weight. Only weight-bearing lifts flow into the weight tiers below.
  const group = groupOf(name)
  const weightBearing = !!group && !isRepsOnly(name)
  if (!weightBearing) {
    return { kind: 'seed', target: ex.t, ph: targetPlaceholders(ex.t) }
  }

  // Tier 1 - real history for THIS exercise (double progression, always greater than last).
  const last = lastLoggedSet(history, name, beforeDate)
  if (last && last.w != null && !last.repsOnly) {
    const assist = ASSISTANCE_EXERCISES.has(name)
    const prog = nextTargetFromHistory(ex, last.w, last.r, assist)
    return {
      kind: 'history',
      target: prog.str,
      ph: { w: fmtNum(prog.w), r: String(prog.reps) },
      lastStr: assist ? `asistencia ${fmtLast(last)}` : fmtLast(last), // menos asistencia = más fuerte
    }
  }

  // Tier 2a - conservative estimate from a related lift with LOGGED history.
  const est = relatedEstimate(name, history, beforeDate, ex)
  if (est) return est

  // Own plan-seed - THIS lift's own concrete kg beats the plan-seed-proxy and profile estimates below,
  // so a coach-authored number ("41 kg x 8-10", goblet "20 kg", DB curl "12.5 kg") is never downgraded.
  if (/kg/i.test(ex.t)) {
    return { kind: 'seed', target: ex.t, ph: targetPlaceholders(ex.t) }
  }

  // Tier 2b - estimate from a related lift's PLAN-SEED weight (never logged).
  const est2 = relatedSeedEstimate(name, ex)
  if (est2) return est2

  // Tier 2c - profile-based starting weight (free-weight lifts only; null if bodyweight unknown).
  if (profile && currentBw != null) {
    const pe = profileEstimate(name, profile, currentBw, todayIso ?? beforeDate)
    if (pe) {
      const repStr = repStrOf(ex)
      return {
        kind: 'suggest-profile',
        target: `~${fmtNum(pe.w)} kg${pe.perHand ? ' por mano' : ''} × ${repStr}`,
        ph: { w: fmtNum(pe.w), r: repStr },
        bodyWeight: currentBw,
        perHand: pe.perHand,
      }
    }
  }

  // Tier 2d - machine/cable REFERENCE (weaker claim than 2c: "las máquinas varían por marca").
  // Assistance machines render inverted semantics ("asistencia ~X kg": more kg = easier).
  {
    const mr = machineReference(name, profile ?? null, currentBw, todayIso ?? beforeDate)
    if (mr) {
      const repStr = repStrOf(ex)
      const label = mr.assistance
        ? `asistencia ~${fmtNum(mr.w)} kg × ${repStr}`
        : `~${fmtNum(mr.w)} kg${mr.perSide ? ' por lado' : ''} × ${repStr}`
      return {
        kind: 'suggest-reference',
        target: label,
        ph: { w: fmtNum(mr.w), r: repStr },
        bodyWeight: currentBw ?? undefined,
        perHand: mr.perSide,
      }
    }
  }

  // Tier 3 - first time. Free-weight barbell lifts whose honest estimate fell below the empty bar get
  // a specific implement cue (never a fabricated 20 kg); everything else the generic RIR cue.
  const std = STARTING_STANDARDS[name]
  const cue = std?.barbell
    ? 'empieza con mancuernas o una barra ligera, dejando 2-3 reps en reserva'
    : 'primera vez: elige un peso dejando 2 reps en reserva'
  return {
    kind: 'first',
    target: '',
    cue,
    ph: { w: '', r: repStrOf(ex) },
  }
}
